import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { minorToMajor } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateCompanySubscription } from "@/features/fee-engine/api/use-create-company-subscription";
import { useSubscriptionPlan } from "@/features/fee-engine/api/use-subscription-plan";
import type { CompanySubscriptionDto } from "@/features/fee-engine/api/dto";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";
import {
  createCompanySubscriptionSchema,
  type CompanySubscriptionFormValues,
} from "@/features/fee-engine/schemas/company-subscription.schema";
import { SUBSCRIPTION_STATUSES } from "@/features/shared/api/enums";
import {
  usePartySelectOptions,
  useSubscriptionPlanSelectOptions,
} from "@/features/shared/api";

const UUID_REGEXP =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const EMPTY_VALUES: CompanySubscriptionFormValues = {
  companyPartyId: "",
  planId: "",
  status: "ACTIVE",
  startDate: "",
  nextBillingDate: "",
};

const toPayload = (
  values: CompanySubscriptionFormValues
): CompanySubscriptionDto => ({
  companyPartyId: values.companyPartyId.trim(),
  planId: values.planId.trim(),
  status: values.status,
  startDate: values.startDate,
  nextBillingDate: values.nextBillingDate?.trim() || undefined,
});

/**
 * Formulaire de souscription d'une entreprise à un plan
 * (`POST /CompanySubscriptions/add`, guide §6.5). Preview OBLIGATOIRE
 * avec section frais alimentée par le prix mensuel du plan sélectionné
 * (chargé via `useSubscriptionPlan(planId)`).
 */
export function CompanySubscriptionFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useFeeEngineTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createCompanySubscriptionSchema(), [i18n.language]);

  const create = useCreateCompanySubscription();
  const [preview, setPreview] = useState<CompanySubscriptionDto | null>(null);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);
  const { options: planOptions, isLoading: plansLoading } =
    useSubscriptionPlanSelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  // Charge le plan sélectionné pour afficher son prix mensuel dans la preview.
  const planIdForPreview = preview?.planId ?? "";
  const planQuery = useSubscriptionPlan(
    planIdForPreview,
    Boolean(planIdForPreview) && UUID_REGEXP.test(planIdForPreview)
  );

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("fees.subscriptions.feedback.created"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("fees.subscriptions.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="companyPartyId">
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="companyPartyId">
                    {t("fees.subscriptions.form.companyPartyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="companyPartyId"
                    options={partyOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t(
                      "fees.subscriptions.form.companyPartyIdPlaceholder"
                    )}
                    isLoading={partiesLoading}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="planId">
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="planId">
                    {t("fees.subscriptions.form.planId")}
                  </Label>
                  <EntitySearchableSelect
                    id="planId"
                    options={planOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t("fees.subscriptions.form.planIdPlaceholder")}
                    isLoading={plansLoading}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="status">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.subscriptions.form.status")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as CompanySubscriptionFormValues["status"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBSCRIPTION_STATUSES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="startDate">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="startDate">
                    {t("fees.subscriptions.form.startDate")}
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    className="notranslate"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="nextBillingDate">
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="nextBillingDate">
                    {t("fees.subscriptions.form.nextBillingDate")}
                  </Label>
                  <Input
                    id="nextBillingDate"
                    type="date"
                    className="notranslate"
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("fees.subscriptions.preview.createTitle")}
          description={t("fees.subscriptions.preview.createDescription")}
          mode="user"
          fees={[]}
          total={
            planQuery.data
              ? {
                  label: t("fees.subscriptions.preview.totalLabel"),
                  amount: minorToMajor(
                    planQuery.data.monthlyPriceMinor ?? 0,
                    planQuery.data.currency ?? "GNF"
                  ),
                  currency: planQuery.data.currency ?? "GNF",
                }
              : undefined
          }
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
